/**
 * Who sees the sample sign-up dialog, and where "Continuar treinando" goes.
 *
 * Logged out means there is no access token. That is the same signal the
 * login screen uses (`AuthService.getAccessToken`). A profile seed with no
 * token is ignored: the seed can outlive the session, and no token is
 * logged out.
 *
 * A token whose profile store is still idle or loading is pending. The
 * sample result is already decided and can render. The dialog stays closed
 * until the store leaves that wait, and it opens only for a logged-out
 * visitor. This module does not read or write storage and does not record
 * the chosen letter. The caller passes the token and the store snapshot.
 *
 * "Continuar treinando" uses `hasProfile(profile, PAID)`, the same split
 * `Login.view.tsx` uses after sign-in. administrador, master, basico, and
 * competidor go to `/home`. free, simulado, a missing profile, and a load
 * that finished in error while the token is still present go to `/planos`.
 */

import { hasProfile, PAID } from "../../auth/accessPolicy";
import type { SampleCheckPhase } from "./sampleAnswerFlow";

export type SampleProfileStatus = "idle" | "loading" | "ready" | "error";

export type SampleContinueTarget = "/home" | "/planos";

export type SampleVisitor =
  | { status: "logged-out" }
  | { status: "pending" }
  | { status: "logged-in"; continueTo: SampleContinueTarget };

/** Section control after a check. `none` leaves Responder as the only action. */
export type SampleSectionCta =
  | { kind: "none" }
  | { kind: "sign-up"; primary: boolean }
  | { kind: "continue"; to: SampleContinueTarget };

export const SAMPLE_SIGN_UP_LABEL = "Criar conta";
export const SAMPLE_CONTINUE_LABEL = "Continuar treinando";

export function resolveSampleVisitor(input: {
  token: string | null;
  profileStatus: SampleProfileStatus;
  profile: string | null | undefined;
}): SampleVisitor {
  if (!input.token) {
    return { status: "logged-out" };
  }
  if (input.profileStatus === "idle" || input.profileStatus === "loading") {
    return { status: "pending" };
  }
  const continueTo: SampleContinueTarget = hasProfile(input.profile, PAID)
    ? "/home"
    : "/planos";
  return { status: "logged-in", continueTo };
}

/**
 * Logged out: "Criar conta" after a failure (not the primary) and after a
 * right or wrong result (the only primary). It is absent while the visitor
 * is still choosing or the check is in flight, so the block does not prompt
 * for an account before a result.
 * Logged in: "Continuar treinando" only after a right or wrong result.
 * Pending: neither control. The result can still render.
 */
export function sampleSectionCta(input: {
  visitor: SampleVisitor;
  phase: SampleCheckPhase;
}): SampleSectionCta {
  const answered = input.phase === "right" || input.phase === "wrong";
  if (input.visitor.status === "logged-out") {
    if (answered) return { kind: "sign-up", primary: true };
    if (input.phase === "failed") return { kind: "sign-up", primary: false };
    return { kind: "none" };
  }
  if (input.visitor.status === "logged-in" && answered) {
    return { kind: "continue", to: input.visitor.continueTo };
  }
  return { kind: "none" };
}

/** A result replaces Responder. Ready, checking, and failed keep it. */
export function sampleKeepsResponder(phase: SampleCheckPhase): boolean {
  return phase !== "right" && phase !== "wrong";
}

/**
 * The section's "Criar conta" opens the dialog only after a successful
 * check. A failure leaves the control visible and does not open the dialog
 * or send the visitor to `/login` or `/registro`.
 */
export function sampleSignUpOpensDialog(phase: SampleCheckPhase): boolean {
  return phase === "right" || phase === "wrong";
}

/**
 * Auto-open once, after a right or wrong result, for a logged-out visitor.
 * Closing sets `dismissed`. A later click is not this function: the section
 * sets `open` itself.
 */
export function sampleDialogShouldAutoOpen(input: {
  visitor: SampleVisitor;
  phase: SampleCheckPhase;
  dismissed: boolean;
  open: boolean;
}): boolean {
  if (input.open || input.dismissed) return false;
  if (input.visitor.status !== "logged-out") return false;
  return sampleSignUpOpensDialog(input.phase);
}
